// bug-177: reconcile a briefing link's recipient against the active dashboard session.
//
// A briefing DM/email link lands here as `app.terum.ai/#/team-today?b=<token>`. The
// token is a signed, NON-CREDENTIAL claim minted by the MVP backend that only NAMES the
// intended recipient — it grants no access. We decode it via the MVP `verify-intent`
// endpoint (so SIGNING_SECRET stays server-side) and compare the named `sub` to the
// session user. Mismatch -> show a "wrong account" banner instead of silently rendering
// someone else's briefing.
//
// FAIL-SAFE: the MVP endpoint deploys BEFORE this SPA (per the deploy order), so this
// code must never break if the endpoint is momentarily absent. Any 404 / network error /
// non-OK / { valid:false } response resolves to "no intent" -> render normally, no banner.

const API_BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "";

// sessionStorage key holding a briefing token across the OAuth round-trip (the OAuth
// redirect drops the URL hash where ?b= lives, so we stash it before sign-in).
const STASH_KEY = "terum-briefing-intent-token";

export interface BriefingIntent {
  sub: string;
  email: string | null;
}

/**
 * Extract the `b` token from the current URL. With HashRouter the query lives inside the
 * hash (`#/team-today?b=...`), but a token may also appear in the real search string, so
 * check both. Returns null when absent.
 */
export function readBriefingTokenFromUrl(): string | null {
  // Hash form: "#/team-today?b=<token>"
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  if (qIndex !== -1) {
    const fromHash = new URLSearchParams(hash.slice(qIndex + 1)).get("b");
    if (fromHash) return fromHash;
  }
  // Plain search form: "?b=<token>" (defensive — some redirects land here)
  const fromSearch = new URLSearchParams(window.location.search).get("b");
  return fromSearch || null;
}

/** Remove the `?b=` param from the hash URL without a navigation (history.replaceState). */
export function stripBriefingTokenFromUrl(): void {
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return;
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  if (!params.has("b")) return;
  params.delete("b");
  const rest = params.toString();
  const newHash = hash.slice(0, qIndex) + (rest ? `?${rest}` : "");
  const base = window.location.pathname + window.location.search;
  window.history.replaceState(null, "", base + newHash);
}

/** Stash a token before the OAuth redirect (which drops the hash). */
export function stashBriefingTokenForLogin(token: string): void {
  try {
    sessionStorage.setItem(STASH_KEY, token);
  } catch {
    // sessionStorage can throw (private mode / quota) — non-fatal; the banner just won't
    // re-appear after login. Render-normally is the safe degradation.
  }
}

/** Read and clear any token stashed before the OAuth round-trip. */
export function popStashedBriefingToken(): string | null {
  try {
    const token = sessionStorage.getItem(STASH_KEY);
    if (token) sessionStorage.removeItem(STASH_KEY);
    return token;
  } catch {
    return null;
  }
}

/**
 * Decode a briefing-intent token via the MVP endpoint. FAIL-SAFE: returns null (treated
 * as "no intent" -> render normally) on a missing token, a non-OK response, a network
 * failure, or a { valid:false } body. NEVER throws.
 */
export async function verifyBriefingIntent(token: string | null): Promise<BriefingIntent | null> {
  if (!token) return null;
  try {
    const res = await fetch(
      `${API_BASE}/api/briefing/verify-intent?b=${encodeURIComponent(token)}`,
      { method: "GET" }
    );
    if (!res.ok) return null; // 404 (endpoint not yet deployed) / 5xx -> fail safe
    const body = (await res.json()) as { valid?: boolean; sub?: string; email?: string | null };
    if (!body?.valid || typeof body.sub !== "string" || !body.sub) return null;
    return { sub: body.sub, email: typeof body.email === "string" ? body.email : null };
  } catch {
    return null; // network error -> fail safe (no banner)
  }
}

export type ReconcileResult =
  | { kind: "none" } // no token / unverifiable -> render normally
  | { kind: "match" } // intended recipient === session user -> render normally
  | { kind: "mismatch"; intendedEmail: string | null }; // wrong account -> banner

/**
 * Compare the verified intent against the active session user id. Pure decision logic.
 */
export function reconcileBriefingIntent(
  intent: BriefingIntent | null,
  sessionUserId: string | null | undefined
): ReconcileResult {
  if (!intent) return { kind: "none" };
  if (sessionUserId && intent.sub === sessionUserId) return { kind: "match" };
  return { kind: "mismatch", intendedEmail: intent.email };
}
